import { RefObject, useEffect, useState } from "react";

/**
 * 스크롤 컨테이너가 바닥에 닿았는지를 알려주는 훅.
 *
 * window가 아니라 임의의 overflow 요소를 대상으로 한다 —
 * 패스픽은 페이지마다 자체 `overflow: auto` Box가 스크롤을 담당하고 body는 스크롤되지 않는다.
 *
 * 스크롤 이벤트뿐 아니라 ResizeObserver로도 재계산한다. 목록이 늦게 채워지거나
 * 페이지 크기가 바뀌면 스크롤 없이도 바닥 여부가 달라지기 때문이다.
 */
export default function useScrolledToBottom(
  ref: RefObject<HTMLElement>,
  threshold = 24
): boolean {
  const [isAtBottom, setIsAtBottom] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) {
      return;
    }

    const update = () => {
      const remaining = el.scrollHeight - el.scrollTop - el.clientHeight;
      setIsAtBottom(remaining <= threshold);
    };

    update();
    el.addEventListener("scroll", update, { passive: true });

    // 컨테이너 자신의 크기 변화(창 리사이즈)와 내용 높이 변화(목록 로드/페이지 크기 변경)를
    // 모두 잡아야 한다. 후자는 컨테이너가 아니라 내부 콘텐츠 요소가 커지는 것이라 함께 관찰한다.
    const resizeObserver = new ResizeObserver(update);
    resizeObserver.observe(el);
    if (el.firstElementChild) {
      resizeObserver.observe(el.firstElementChild);
    }

    return () => {
      el.removeEventListener("scroll", update);
      resizeObserver.disconnect();
    };
  }, [ref, threshold]);

  return isAtBottom;
}
